import { useState } from "react";
import PageHeader from "@/components/common/PageHeader";
import SeoHead from "@/components/common/SeoHead";
import { api, formatApiError, getImageUrl } from "@/lib/api";
import { toast } from "sonner";
import { HandHeart, Sparkles, Users, Award, ArrowRight, CheckCircle2 } from "lucide-react";
import { useSettings } from "@/context/SettingsContext";

const BENEFITS = [
  { icon: Users, title: "Dampak Nyata", body: "Terlibat langsung dalam program yang mengubah kehidupan masyarakat." },
  { icon: Award, title: "Sertifikat & Pengakuan", body: "Sertifikat resmi dan pengakuan atas kontribusi Anda." },
  { icon: Sparkles, title: "Pengembangan Diri", body: "Pelatihan kepemimpinan, kolaborasi, dan keterampilan sosial." },
  { icon: HandHeart, title: "Komunitas Bermakna", body: "Bergabung dengan jaringan relawan lintas latar belakang." },
];

const REQUIREMENTS = [
  "Berusia minimal 17 tahun",
  "Berkomitmen pada nilai kemanusiaan dan integritas",
  "Bersedia mengikuti orientasi & pelatihan singkat",
  "Waktu fleksibel (minimum 4 jam/bulan)",
];

const PLACEHOLDER_FAQ = [
  { q: "Apakah menjadi relawan Sauyunan berbayar?", a: "Tidak, semua kegiatan volunteer bersifat sukarela. Namun kami dapat menyediakan reimbursement untuk biaya transport program lapangan tertentu." },
  { q: "Program apa yang bisa saya ikuti?", a: "Anda dapat memilih dari 7 bidang program strategis kami — kemanusiaan, pangan, kesehatan, pendidikan, lingkungan, ekonomi, atau inovasi." },
  { q: "Berapa lama proses seleksi?", a: "Umumnya 3–7 hari kerja. Tim kami akan menghubungi Anda melalui email atau telepon setelah mengevaluasi formulir Anda." },
  { q: "Bisakah saya menjadi relawan secara remote?", a: "Ya! Beberapa program kami — seperti literasi digital, riset, dan pengembangan konten — mendukung volunteer remote." },
];

const INTERESTS = [
  "Humanity & Resilience",
  "Food Systems & Bioeconomy",
  "Health & Nutrition",
  "Education & Leadership",
  "Green Impact & Climate",
  "Economic Empowerment",
  "Future Innovation",
];

export default function Volunteer() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", city: "", interest: INTERESTS[0], motivation: "" });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);
  const { settings } = useSettings();
  const pageData = settings?.volunteer_page || {};
  const faqs = pageData.faq && pageData.faq.length > 0 ? pageData.faq : PLACEHOLDER_FAQ;

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/volunteer", form);
      setSuccess(true);
      toast.success("Terima kasih! Pendaftaran Anda telah kami terima.");
      setForm({ name: "", email: "", phone: "", city: "", interest: INTERESTS[0], motivation: "" });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="page-volunteer">
      <SeoHead
        title={{ id: "Volunteer", en: "Volunteer" }}
        description={{ id: pageData.description || "Bergabunglah dengan gerakan Silih Asih Sauyunan Foundation sebagai relawan dan berkontribusi pada ekosistem dampak nyata.", en: "Join Sauyunan Foundation's Silih Asih movement as a volunteer and contribute to a real impact ecosystem." }}
      />
      <PageHeader
        eyebrow={pageData.eyebrow || "Volunteer"}
        title={pageData.title || "Bergabunglah dengan gerakan Silih Asih"}
        description={pageData.description || "Menjadi relawan bukan sekadar memberi waktu — Anda ikut membangun ekosistem dampak yang menyentuh generasi."}
        image={pageData.image ? getImageUrl(pageData.image) : "https://images.unsplash.com/photo-1747778148316-4a8eeb132d48?w=1200&q=80"}
      />

      {/* Benefits */}
      <section className="section-y bg-white">
        <div className="container-sf">
          <div className="max-w-3xl mb-12">
            <div className="eyebrow mb-3">Manfaat</div>
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 leading-tight">
              Kenapa menjadi relawan Sauyunan?
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            {BENEFITS.map((b, i) => (
              <div key={i} className="card-sf p-6" data-testid={`vol-benefit-${i}`}>
                <div className={`h-12 w-12 rounded-xl ${i % 2 === 0 ? "bg-[var(--brand-blue)]" : "bg-[var(--brand-orange)]"} text-white flex items-center justify-center mb-4`}>
                  <b.icon className="h-5 w-5" />
                </div>
                <h3 className="text-lg font-bold text-slate-900">{b.title}</h3>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">{b.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Form + Requirements */}
      <section className="section-y bg-[var(--bg)]">
        <div className="container-sf grid lg:grid-cols-[1fr,1.3fr] gap-10">
          <div>
            <div className="eyebrow mb-3">Persyaratan</div>
            <h3 className="text-3xl font-bold text-slate-900 leading-tight mb-6">Siap bergabung? Ini yang Anda perlukan.</h3>
            <ul className="space-y-3 mb-8">
              {REQUIREMENTS.map((r, i) => (
                <li key={i} className="flex gap-3 text-slate-700"><CheckCircle2 className="h-5 w-5 text-[var(--brand-orange)] shrink-0 mt-0.5" /> {r}</li>
              ))}
            </ul>
            <div className="rounded-2xl bg-white border border-slate-200 p-6">
              <div className="text-xs font-semibold uppercase tracking-wider text-[var(--brand-orange)] mb-2">Proses</div>
              <ol className="space-y-3 text-sm text-slate-700">
                <li className="flex gap-3"><span className="font-bold text-[var(--brand-blue)]">01</span> Isi formulir pendaftaran</li>
                <li className="flex gap-3"><span className="font-bold text-[var(--brand-blue)]">02</span> Tim kami menghubungi via email/telepon (3–7 hari)</li>
                <li className="flex gap-3"><span className="font-bold text-[var(--brand-blue)]">03</span> Orientasi & pelatihan singkat</li>
                <li className="flex gap-3"><span className="font-bold text-[var(--brand-blue)]">04</span> Mulai berkontribusi bersama komunitas!</li>
              </ol>
            </div>
          </div>

          <div>
            {success ? (
              <div className="card-sf p-10 text-center" data-testid="volunteer-success">
                <CheckCircle2 className="h-14 w-14 text-[var(--brand-orange)] mx-auto mb-4" />
                <h3 className="text-2xl font-bold text-slate-900 mb-2">Terima kasih!</h3>
                <p className="text-slate-600 mb-6">Pendaftaran Anda telah kami terima. Silakan cek email Anda untuk konfirmasi. Tim kami akan menghubungi Anda dalam 3–7 hari kerja.</p>
                <button onClick={() => setSuccess(false)} className="btn-outline">Daftarkan orang lain</button>
              </div>
            ) : (
              <form onSubmit={submit} className="card-sf p-8" data-testid="volunteer-form">
                <h3 className="text-2xl font-bold text-slate-900 mb-6">Formulir Pendaftaran Relawan</h3>
                <div className="grid sm:grid-cols-2 gap-4">
                  <Input label="Nama Lengkap" required value={form.name} onChange={(v) => setForm({ ...form, name: v })} testId="vol-name" />
                  <Input label="Email" type="email" required value={form.email} onChange={(v) => setForm({ ...form, email: v })} testId="vol-email" />
                  <Input label="Nomor Telepon" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} testId="vol-phone" />
                  <Input label="Kota / Domisili" value={form.city} onChange={(v) => setForm({ ...form, city: v })} testId="vol-city" />
                </div>
                <div className="mt-4">
                  <label className="block text-xs font-semibold text-slate-700 mb-1.5">Bidang Minat</label>
                  <select
                    value={form.interest}
                    onChange={(e) => setForm({ ...form, interest: e.target.value })}
                    data-testid="vol-interest"
                    className="w-full px-4 py-3 rounded-xl border border-slate-200 text-sm bg-white focus:outline-none focus:border-[var(--brand-blue)]"
                  >
                    {INTERESTS.map((i) => <option key={i}>{i}</option>)}
                  </select>
                </div>
                <div className="mt-4">
                  <label className="block text-xs font-semibold text-slate-700 mb-1.5">Motivasi <span className="text-slate-400">(opsional)</span></label>
                  <textarea
                    rows={4}
                    value={form.motivation}
                    onChange={(e) => setForm({ ...form, motivation: e.target.value })}
                    data-testid="vol-motivation"
                    className="w-full px-4 py-3 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-[var(--brand-blue)]"
                    placeholder="Ceritakan sedikit tentang motivasi Anda..."
                  />
                </div>
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="vol-submit"
                  className="btn-primary mt-6 w-full justify-center"
                >
                  {submitting ? "Mengirim..." : "Daftar Sekarang"} <ArrowRight className="h-4 w-4" />
                </button>
              </form>
            )}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="section-y bg-white">
        <div className="container-sm">
          <div className="text-center mb-10">
            <div className="eyebrow mb-3">FAQ</div>
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 leading-tight">Pertanyaan yang sering ditanyakan</h2>
          </div>
          <div className="space-y-3">
            {faqs.map((f, i) => (
              <div key={i} className="card-sf overflow-hidden" data-testid={`faq-${i}`}>
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full text-left px-5 py-4 flex items-center justify-between gap-4"
                >
                  <span className="font-bold text-slate-900">{f.q}</span>
                  <span className={`text-2xl text-[var(--brand-orange)] transition ${openFaq === i ? "rotate-45" : ""}`}>+</span>
                </button>
                {openFaq === i && <div className="px-5 pb-5 text-slate-600 leading-relaxed whitespace-pre-wrap">{f.a}</div>}
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}

function Input({ label, type = "text", value, onChange, required, testId }) {
  return (
    <div>
      <label className="block text-xs font-semibold text-slate-700 mb-1.5">{label}{required && <span className="text-[var(--brand-orange)]"> *</span>}</label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        data-testid={testId}
        className="w-full px-4 py-3 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-[var(--brand-blue)]"
      />
    </div>
  );
}
